'use client';

import { c as _c } from "react/compiler-runtime";
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { useCallback, useEffect, useState } from 'react';
import { cx } from 'class-variance-authority';
import { ButtonLink, Icon, type IconName } from '~/components/primitives';
import { AnimatePresence, motion, type Transition } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { I18N_CONF } from '@funkey/locales/config';
import LocaleSelector from '~/features/i18n/LanguageSelect/LocaleSelector';
import Logo from '~/funkey/Logo';
import type { SB_HeaderMenuItemProps, SB_HeaderProductMenuItemProps, SB_HeaderProps } from '~/storyblok/components/types.generated';
import type { action_getFeaturedActivityCategories } from '~/app/[locale]/(web)/_data/actions';
import { StoryblokLink } from '~/storyblok/utils/StoryblokLink';
import AccountButton from '~/features/auth/user/AccountButton';
import { useI18n } from '~/features/i18n';
import { SubMenuContent } from './WebMobileSubMenuContent';
import { SubMenuListColWrapper } from './WebMobileSubMenuItem';
import { transformStoryblokLink } from '~/storyblok/components/utils';
import { ChevronRight } from 'lucide-react';
namespace NavTransition {
  export const DEFAULT_TRANSITION = {
    type: 'spring',
    stiffness: 100,
    damping: 10,
    duration: 0.5
  } as const satisfies Transition;
  export const override = (options: Transition) => ({
    ...DEFAULT_TRANSITION,
    ...options
  });
}
const WebMobileNav: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  story: SB_HeaderProps;
  featuredCategories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
}> = props => {
  const $ = _c(17);
  const [currentView, setCurrentView] = useState("main");
  const [currentSubMenuId, setSurrentSubMenuId] = useState(null);
  const path = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      document.body.style.removeProperty("overflow");
      setCurrentView("main");
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const unmount = t0;
  let t1;
  if ($[1] !== props) {
    t1 = () => {
      props.onClose();
      return unmount;
    };
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== path) {
    t2 = [path];
    $[3] = path;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== props.isOpen) {
    t3 = () => {
      if (props.isOpen) {
        document.body.style.setProperty("overflow", "hidden");
      }
      return unmount;
    };
    t4 = [props.isOpen];
    $[5] = props.isOpen;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== currentSubMenuId || $[9] !== currentView || $[10] !== props.featuredCategories || $[11] !== props.isOpen || $[12] !== props.onClose || $[13] !== props.story) {
    t5 = props.isOpen && <motion.div initial="hidden" animate="visible" exit="hidden" variants={{
      hidden: {
        y: "-100%"
      },
      visible: {
        y: "0%"
      }
    }} transition={NavTransition.override({
      duration: 1
    })} className="fixed inset-0 z-10 text-white" aria-modal="true"><NavigationMenuPrimitive.Root className="relative h-full w-full after:absolute after:top-[-180px] after:right-0 after:left-0 after:block after:h-[200px] after:bg-blue" onValueChange={value => {
        setCurrentView(value === "" ? "main" : "submenu");
        setSurrentSubMenuId(value || null);
      }}><motion.div transition={NavTransition.DEFAULT_TRANSITION} variants={{
          menu: {
            x: "0px",
            y: "0px",
            rotate: -10
          },
          submenu: {
            x: "-43vw",
            y: "10px",
            rotate: 0
          },
          hidden: {
            x: "200px",
            y: "-40px",
            rotate: -10
          }
        }} initial="hidden" exit="hidden" data-test={currentView === "submenu" ? "submenu" : "menu"} animate={currentView === "submenu" ? "submenu" : "menu"} className="absolute right-[40px] bottom-[70px] z-30"><Logo className="h-auto w-[100px]" /></motion.div><motion.button data-testid="ModalCloseButton" type="button" className="absolute top-7 right-5 z-25 p-2.5 text-white" onClick={props.onClose} transition={NavTransition.DEFAULT_TRANSITION} animate={{
          rotate: currentView === "main" ? 180 : 0
        }}><Icon name="cross" className="text-inherit" /></motion.button><motion.div className="pointer-events-none fixed inset-0 z-0 h-full w-full bg-yellow-500" transition={NavTransition.override({
          delay: 0.1
        })} /><motion.div className="pointer-events-none fixed z-1 h-full w-[250%] -translate-x-[30%] -translate-y-[6%] rounded-[100%] bg-blue" transition={NavTransition.DEFAULT_TRANSITION} /><div className="relative z-20 h-full w-full bg-transparent"><div className={cx("isolate h-full w-full overflow-x-clip overflow-y-auto", "from-transparent to-transparent before:pointer-events-none before:absolute before:top-0 before:right-0 before:left-0 before:z-30 before:block before:h-15 before:bg-linear-to-b", "from-transparent to-transparent after:pointer-events-none after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-[30vh] after:bg-linear-to-t")}><motion.div className="pt-15 pb-[180px]" initial="main" animate={currentView === "main" ? "main" : "submenu"} transition={NavTransition.DEFAULT_TRANSITION} variants={{
              main: {
                x: 0
              },
              submenu: {
                x: "-100%"
              }
            }}><MenuContent currentSubMenuId={currentSubMenuId ?? undefined} menuItems={props.story.menuItems ?? []} categories={props.featuredCategories} ctaItems={props.story.ctaItems ?? []} /></motion.div></div></div></NavigationMenuPrimitive.Root></motion.div>;
    $[8] = currentSubMenuId;
    $[9] = currentView;
    $[10] = props.featuredCategories;
    $[11] = props.isOpen;
    $[12] = props.onClose;
    $[13] = props.story;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <AnimatePresence>{t5}</AnimatePresence>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
export default WebMobileNav;
const MenuContent: React.FC<{
  menuItems: Array<SB_HeaderMenuItemProps | SB_HeaderProductMenuItemProps>;
  categories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
  ctaItems: SB_HeaderMenuItemProps[];
  className?: string;
  currentSubMenuId?: string;
}> = t0 => {
  const $ = _c(18);
  const {
    menuItems,
    currentSubMenuId,
    categories,
    ctaItems,
    className
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] !== className) {
    t1 = cx("w-full", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== categories || $[3] !== currentSubMenuId || $[4] !== menuItems) {
    t2 = <MenuItems.Component menuItems={menuItems} currentSubMenuId={currentSubMenuId} categories={categories} />;
    $[2] = categories;
    $[3] = currentSubMenuId;
    $[4] = menuItems;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== ctaItems || $[7] !== i18n) {
    let t4;
    if ($[9] !== i18n) {
      t4 = x => <ButtonLink key={x._uid} size="lg" href={transformStoryblokLink(x.link ?? {}, i18n.locale).href} className="my-7 w-full" variant="tertiary">{x.text}</ButtonLink>;
      $[9] = i18n;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = ctaItems.map(t4);
    $[6] = ctaItems;
    $[7] = i18n;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col font-medium text-white"><LocaleSelector className="gap-x-3 p-3 leading-6" /><AccountButton full={true} /></div>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3) {
    t5 = <div className="px-10">{t3}{t4}</div>;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t5) {
    t6 = <section className={t1}>{t2}{t5}</section>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
namespace MenuItems {
  export const Component: React.FC<{
    menuItems: Array<SB_HeaderMenuItemProps | SB_HeaderProductMenuItemProps>;
    categories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
    currentSubMenuId?: string;
  }> = props => {
    const $ = _c(16);
    const i18n = useI18n();
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        position: "unset"
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Icon name="home-2" className="shrink-0 text-blue-400" aria-hidden="true" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== i18n) {
      t2 = i18n._("Home");
      $[2] = i18n;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <NavigationMenuPrimitive.Item><Link href="/" className="flex gap-x-3 p-3 leading-6 font-medium">{t1}<span>{t2}</span></Link></NavigationMenuPrimitive.Item>;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== props.categories || $[7] !== props.currentSubMenuId || $[8] !== props.menuItems) {
      let t5;
      if ($[10] !== props.categories || $[11] !== props.currentSubMenuId) {
        t5 = menuItem => <NavigationMenuPrimitive.Item value={menuItem._uid} key={menuItem._uid}>{"layout" in menuItem && menuItem.layout ? <MenuContent menuItem={menuItem as any} currentSubMenuId={props.currentSubMenuId} categories={props.categories} /> : menuItem.component === "HeaderMenuItem" ? <MenuLink {...menuItem} /> : null}</NavigationMenuPrimitive.Item>;
        $[10] = props.categories;
        $[11] = props.currentSubMenuId;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      t4 = props.menuItems.map(t5);
      $[6] = props.categories;
      $[7] = props.currentSubMenuId;
      $[8] = props.menuItems;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[13] !== t3 || $[14] !== t4) {
      t5 = <NavigationMenuPrimitive.List style={t0} asChild={true}><ul className="flex flex-col px-10 text-white">{t3}{t4}</ul></NavigationMenuPrimitive.List>;
      $[13] = t3;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  };
  const MenuLink: React.FC<SB_HeaderMenuItemProps> = menuItem => {
    const $ = _c(4);
    let t0;
    if ($[0] !== menuItem.link || $[1] !== menuItem.mobileIcon || $[2] !== menuItem.text) {
      t0 = menuItem.link && <StoryblokLink type="link" link={menuItem.link} className="flex gap-x-3 p-3 leading-6 font-medium"><Icon name={menuItem.mobileIcon as IconName} className="shrink-0 text-blue-400" aria-hidden="true" /><span>{menuItem.text}</span></StoryblokLink>;
      $[0] = menuItem.link;
      $[1] = menuItem.mobileIcon;
      $[2] = menuItem.text;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  };
  const MenuContent: React.FC<{
    menuItem: SB_HeaderMenuItemProps;
    categories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
    currentSubMenuId?: string;
  }> = props => {
    const $ = _c(17);
    const t0 = props.menuItem.mobileIcon as IconName;
    let t1;
    if ($[0] !== t0) {
      t1 = <Icon name={t0} className="shrink-0 text-blue-400" aria-hidden="true" />;
      $[0] = t0;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== props.menuItem.text) {
      t2 = <span>{props.menuItem.text}</span>;
      $[2] = props.menuItem.text;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ChevronRight className="size-3.5 shrink-0 text-blue-400" />;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t1 || $[6] !== t2) {
      t4 = <NavigationMenuPrimitive.Trigger asChild={true}><button type="button" className="flex items-center gap-x-3 p-3 leading-6 font-medium">{t1}{t2}{t3}</button></NavigationMenuPrimitive.Trigger>;
      $[5] = t1;
      $[6] = t2;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== props.categories || $[9] !== props.currentSubMenuId || $[10] !== props.menuItem) {
      t5 = props.currentSubMenuId === props.menuItem._uid && <NavigationMenuPrimitive.Content asChild={true} forceMount={true}><motion.section exit={{
          opacity: 0,
          transition: NavTransition.override({
            delay: NavTransition.DEFAULT_TRANSITION.duration / 2
          })
        }} initial={{
          opacity: 1
        }} className="absolute top-0 -right-full h-full w-full"><NavigationMenuPrimitive.Trigger asChild={true}><button type="button" className="absolute -top-[32px] left-7 z-10 p-2.5 text-white"><Icon name="arrow-left" /></button></NavigationMenuPrimitive.Trigger><NavigationMenuPrimitive.Sub asChild={true}><SubMenuContent item={props.menuItem}>{props.menuItem.layout === "SUBNAV_SPOTLIGHT" ? <SubMenuCategories categories={props.categories} /> : undefined}</SubMenuContent></NavigationMenuPrimitive.Sub></motion.section></NavigationMenuPrimitive.Content>;
      $[8] = props.categories;
      $[9] = props.currentSubMenuId;
      $[10] = props.menuItem;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== t5) {
      t6 = <AnimatePresence>{t5}</AnimatePresence>;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t4 || $[15] !== t6) {
      t7 = <>{t4}{t6}</>;
      $[14] = t4;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  };
}
const SubMenuCategories: React.FC<{
  categories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
}> = props => {
  const $ = _c(16);
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n) {
    t0 = i18n._("Categories");
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== i18n) {
    t1 = i18n._("All activities");
    $[2] = i18n;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== i18n.locale) {
    t2 = I18N_CONF.getTranslatedUrl("teambuildings", {
      locale: i18n.locale
    });
    $[4] = i18n.locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== i18n.locale || $[7] !== props.categories) {
    let t4;
    if ($[9] !== i18n.locale) {
      t4 = category => <Link key={category.id} href={I18N_CONF.getTranslatedUrl("categories", {
        slug: category.slug,
        locale: i18n.locale
      })}>{category.name}</Link>;
      $[9] = i18n.locale;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = props.categories.map(t4);
    $[6] = i18n.locale;
    $[7] = props.categories;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[11] !== t0 || $[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <SubMenuListColWrapper title={t0} linkText={t1} href={t2}>{t3}</SubMenuListColWrapper>;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};